import { eFilterType, eTableName, IFilterSchema, ILCHttpResponse, ISearch, ITableFilter } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export interface IGetFiltersRequest {
  filterType: eFilterType
  tableName: eTableName
}

export interface ICreateFilerDTO {
  tableName: eTableName
  search: ISearch
  type: eFilterType
  groupName?: string
}

export interface IUpdateFilerDTO {
  filterId: number
  search: ISearch
}

export const filtersApi = createApi({
  reducerPath: 'filtersApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/filters`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    getAllFilters: builder.query<ITableFilter[], void>({
      query: () => ({
        url: '/',
        method: 'GET',
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITableFilter[]>, meta) => {
        return rawResult.data as ITableFilter[]
      },
    }),
    getAllFiltersByType: builder.query<ITableFilter[], eFilterType>({
      query: (filterType) => ({
        url: `/${filterType}`,
        method: 'GET',
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITableFilter[]>, meta) => {
        return rawResult.data as ITableFilter[]
      },
    }),
    getAllFiltersByTypeAndTableName: builder.query<ITableFilter[], IGetFiltersRequest>({
      query: ({ filterType, tableName }) => ({
        url: `/${filterType}/${tableName}`,
        method: 'GET',
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITableFilter[]>, meta) => {
        return rawResult.data as ITableFilter[]
      },
    }),
    createFilters: builder.mutation<ITableFilter[], ICreateFilerDTO[]>({
      query: (filters: ICreateFilerDTO[]) => ({
        url: '',
        method: 'POST',
        body: filters,
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITableFilter[]>, meta) => {
        return rawResult.data as ITableFilter[]
      },
    }),
    updateFilters: builder.mutation<{ modifiedCount: number }, IUpdateFilerDTO>({
      query: ({ filterId, search }) => ({
        url: '',
        method: 'PUT',
        body: {
          filterId,
          search,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<{ modifiedCount: number }>, meta) => {
        return rawResult.data as { modifiedCount: number }
      },
    }),
    deleteFilters: builder.mutation<IFilterSchema[], string[]>({
      query: (filtersIds) => ({
        url: '',
        method: 'DELETE',
        body: {
          filtersIds,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IFilterSchema[]>, meta) => {
        return rawResult.data as IFilterSchema[]
      },
    }),
  }),
})

export const {
  useLazyGetAllFiltersQuery,
  useLazyGetAllFiltersByTypeQuery,
  useLazyGetAllFiltersByTypeAndTableNameQuery,
  useCreateFiltersMutation,
  useUpdateFiltersMutation,
  useDeleteFiltersMutation,
} = filtersApi
